import React, { useState } from 'react';
import { Tabs, Tab, Button, Modal } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';

import {
  LocationsTable,
  PrescribersTable,
  PrivateLayout,
  ResourceContent,
  UserSearch,
  UsersTable
} from '../components';
import { FeatureGuard } from '../components/multi-tenant/FeatureGuard';
import { LocationSearch, PrescriberSearchInput } from '../components';
import { getConfigurationDataKey } from '../utils';
import { AddLocationView } from './AddLocation';
import { AddPrescriberView } from './AddPrescriber';
import { AddUserView } from './AddUser';

const users = '#users';
const prescribers = '#prescribers';
const locations = '#locations';
const availableTabs = [users, prescribers, locations];

export const PracticeManagementView = () => {
  const { hash } = useLocation();
  const history = useHistory();

  const defaultActiveKey = availableTabs.includes(hash) ? hash : users;
  const [searchValue, setSearchValue] = useState<string>();
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onTabSelect = (eventKey: string | null) => {
    if (!eventKey) return;
    history.push(eventKey);
  };

  return (
    <FeatureGuard contentKey={'practice-management.locations.manage'}>
      <PrivateLayout
        pageTitle={'practice management'}
        pageIcon={<i className='fa-regular fa-laptop-medical'></i>}
        subtoolbarChildren={
          (hash.includes('locations') && (
            <div className='d-flex align-items-center'>
              <div className='me-3'>
                <LocationSearch onSearch={setSearchValue} />
              </div>
              <FeatureGuard contentKey={'practice-management.locations.manage'}>
                <Button
                  variant='secondary-new'
                  data-testid='navigateToAddLocationBtn'
                  onClick={() => setShowModal(true)}>
                  <i className='fa-light fa-location-dot me-2'></i>
                  Add Location
                </Button>
              </FeatureGuard>
              <Modal show={showModal} onHide={() => setShowModal(false)} size='lg' backdrop='static' centered>
                <Modal.Header>
                  <div className='d-flex w-100 align-items-center justify-content-between'>
                    <Modal.Title as='h5'>Add New Location</Modal.Title>
                    <p className='required-text general-note mb-0 ms-3'>
                      <ResourceContent contentKey={getConfigurationDataKey('general.required-fields-note')} />
                    </p>
                  </div>
                  <button type='button' className='btn-close px-3' onClick={() => setShowModal(false)} />
                </Modal.Header>
                <Modal.Body>
                  {isSubmitting && (
                    <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75'>
                      <img src='../images/spinner.gif' />
                    </div>
                  )}
                  <AddLocationView
                    addedNewLocation={() => setShowModal(false)}
                    setShowModal={setShowModal}
                    setLoading={setIsSubmitting}
                  />
                </Modal.Body>
              </Modal>
            </div>
          )) ||
          (hash.includes('prescribers') && (
            <div className='d-flex align-items-center'>
              <div className='me-3'>
                <PrescriberSearchInput onSearch={setSearchValue} />
              </div>
              <FeatureGuard contentKey={'practice-management.prescribers.manage'}>
                <Button
                  variant='secondary-new pres_text'
                  data-testid='navigateToPrescriberNewBtn'
                  onClick={() => setShowModal(true)}>
                  <i className='fa fa-stethoscope me-2'></i>
                  Add Prescriber
                </Button>
              </FeatureGuard>
              <Modal show={showModal} onHide={() => setShowModal(false)} size='lg' backdrop='static' centered>
                <Modal.Header>
                  <div className='d-flex w-100 align-items-center justify-content-between'>
                    <Modal.Title as='h5'>Add New Prescriber</Modal.Title>
                    <p className='required-text general-note mb-0 ms-3'>
                      <ResourceContent contentKey={getConfigurationDataKey('general.required-fields-note')} />
                    </p>
                  </div>
                  <button type='button' className='btn-close px-3' onClick={() => setShowModal(false)} />
                </Modal.Header>
                <Modal.Body style={{ position: 'relative' }}>
                  {isSubmitting && (
                    <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75'>
                      <img src='../images/spinner.gif' />
                    </div>
                  )}
                  <AddPrescriberView
                    addedNewPrescriber={() => setShowModal(false)}
                    setShowModal={setShowModal}
                    setLoading={setIsSubmitting}
                  />
                </Modal.Body>
              </Modal>
            </div>
          )) ||
          ((hash === '' || hash.includes('users')) && (
            <div className='d-flex align-items-center'>
              <div className='me-3'>
                <UserSearch onSearch={setSearchValue} />
              </div>
              <FeatureGuard contentKey={'practice-management.users.manage'}>
                <Button variant='secondary-new' data-testid='navigateToUserNewBtn' onClick={() => setShowModal(true)}>
                  <i className='fa-light fa-user-injured me-2'></i>
                  Add User
                </Button>
              </FeatureGuard>
              <Modal show={showModal} onHide={() => setShowModal(false)} size='lg' backdrop='static' centered>
                <Modal.Header>
                  <div className='d-flex w-100 align-items-center justify-content-between'>
                    <Modal.Title as='h5'>Add New User</Modal.Title>
                    <p className='required-text general-note mb-0 ms-3'>
                      <ResourceContent contentKey={getConfigurationDataKey('general.required-fields-note')} />
                    </p>
                  </div>
                  <button type='button' className='btn-close px-3' onClick={() => setShowModal(false)} />
                </Modal.Header>
                <Modal.Body>
                  {isSubmitting && (
                    <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75'>
                      <img src='../images/spinner.gif' />
                    </div>
                  )}
                  <AddUserView setLoading={setIsSubmitting} setShowModal={setShowModal} />
                </Modal.Body>
              </Modal>
            </div>
          ))
        }>
        <Tabs
          defaultActiveKey={defaultActiveKey}
          className='tabs-primary'
          mountOnEnter
          onSelect={onTabSelect}
          variant='secondary-new'>
          <Tab eventKey={users} title='Users' className='pt-3 site-background'>
            <UsersTable searchValue={searchValue} />
          </Tab>
          <Tab eventKey={prescribers} title='Prescribers' className='pt-3 site-background'>
            <PrescribersTable searchValue={searchValue} />
          </Tab>
          <Tab eventKey={locations} title='Locations' className='pt-3 site-background'>
            <LocationsTable searchValue={searchValue} />
          </Tab>
        </Tabs>
      </PrivateLayout>
    </FeatureGuard>
  );
};
